import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type React from 'react';
import type { JSONContent } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import {
  sanitizeRichTextContent,
  useTheme,
  type BasicContentBlockA,
  type BasicContentBlockB,
  type BasicContentImageSize,
  type BasicContentLayout,
  type BasicContentTitleLevel,
} from '@course-authoring/shared-types';
import { useIsMobileViewport } from '@course-authoring/ui-blocks';

import { BasicContentLayoutToggle } from './BasicContentLayoutToggle';
import { type ImageAreaState } from './blockEditorStyles';
import { createRichTextEditorExtensions } from './richTextEditorExtensions';
import {
  preventRichTextToolbarMouseDown,
  RichTextToolbar,
} from './richTextToolbar';
import {
  getInteractionRichTextFieldPresetConfig,
  createTokenRichTextToolbarStyles,
} from './interactionEditorUtils';
import { ImageInsertModal, type ImageInsertData } from './ImageInsertModal';
import { useRichTextFieldEditor } from './useRichTextFieldEditor';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import type { TitleLevel } from './TitleLevelSelector';

type BasicContentBlock = BasicContentBlockA | BasicContentBlockB;

const LAYOUT_IMAGE_ASPECT_RATIOS: Record<BasicContentLayout, string> = {
  'image-top': '2:1',
  'image-left': '1:1',
  'image-right': '1:1',
  'no-image': '2:1',
};
const MOBILE_BLOCK_WIDTH = '90%';
const DESKTOP_SPLIT_BLOCK_WIDTH = '80%';
const DESKTOP_IMAGE_RIGHT_GAP = '3.125%';
const DESKTOP_IMAGE_TOP_TEXT_WIDTH = '60%';
const DESKTOP_IMAGE_TOP_IMAGE_WIDTH = '70%';
const DESKTOP_LEFT_INSET = '10%';

const IMAGE_TOP_HEIGHT: Record<BasicContentImageSize, string> = {
  large: '50%',
  medium: '40%',
  small: '30%',
};
const IMAGE_TOP_WIDTH: Record<BasicContentImageSize, string> = {
  large:  '100%',
  medium: '55%',
  small:  '40%',
};

const SPLIT_IMAGE_COLUMNS: Record<BasicContentImageSize, number> = {
  large: 4,
  medium: 3,
  small: 2,
};

function getSplitColumnWidths(imageSize: BasicContentImageSize): { image: string; text: string } {
  const imageCols = SPLIT_IMAGE_COLUMNS[imageSize] ?? 3;
  const textCols = 8 - imageCols;
  const image = `calc(${imageCols} / 8 * (100% - ${DESKTOP_IMAGE_RIGHT_GAP}))`;
  const text = `calc(${textCols} / 8 * (100% - ${DESKTOP_IMAGE_RIGHT_GAP}))`;
  return { image, text };
}

const IMAGE_SIZE_OPTIONS: Array<{ value: BasicContentImageSize; label: string }> = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
];

function resolveLayout(block: BasicContentBlock): BasicContentLayout {
  if (block.layout) {
    return block.layout;
  }

  return block.type === 'basic-content-b' ? 'image-right' : 'image-top';
}

function resolveImageSize(block: BasicContentBlock): BasicContentImageSize {
  return block.imageSize ?? 'medium';
}

function resolveTitleLevel(block: BasicContentBlock): BasicContentTitleLevel {
  return block.titleLevel ?? 'h1';
}

function convertLegacyBodyToRichText(body: string): JSONContent {
  const paragraphs = body
    .split(/\n+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  return {
    type: 'doc',
    content: (paragraphs.length > 0 ? paragraphs : ['']).map((paragraph) => ({
      type: 'paragraph',
      ...(paragraph.length > 0
        ? {
            content: [{ type: 'text', text: paragraph }],
          }
        : {}),
    })),
  };
}

function normalizeBodyContent(body: BasicContentBlock['body']): JSONContent {
  if (typeof body === 'string') {
    return sanitizeRichTextContent(convertLegacyBodyToRichText(body));
  }

  return sanitizeRichTextContent(body);
}

interface BasicContentBlockEditorProps {
  block: BasicContentBlock;
  onChange: (updates: Partial<BasicContentBlock>) => void;
  onSetLayout: (layout: BasicContentLayout) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function BasicContentBlockEditor({
  block,
  onChange,
  onSetLayout,
  autoFocus = false,
  translationMode = false,
}: BasicContentBlockEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageAreaState, setImageAreaState] = useState<ImageAreaState>({
    isFocused: false,
    isHovered: false,
  });
  const [isBodyFocused, setIsBodyFocused] = useState(false);

  const layout = resolveLayout(block);
  const imageSize = resolveImageSize(block);
  const titleLevel = resolveTitleLevel(block);
  const shouldShowImage = layout !== 'no-image';
  const bodyContent = useMemo(() => normalizeBodyContent(block.body), [block.body]);
  const fullPresetConfig = useMemo(() => getInteractionRichTextFieldPresetConfig('full'), []);
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);
  const richTextExtensions = useMemo(
    () =>
      createRichTextEditorExtensions('Enter paragraph text (optional)...', fullPresetConfig.extensionOptions),
    [fullPresetConfig.extensionOptions]
  );

  const bodyEditor = useRichTextFieldEditor({
    extensions: richTextExtensions,
    content: bodyContent,
    onContentChange: (content) => onChange({ body: content }),
    onFocus: () => setIsBodyFocused(true),
    onBlur: () => setIsBodyFocused(false),
    containerRef,
    editorClassName: 'tiptap basic-content-body-editor',
    editorInlineStyle: 'outline: none; min-height: 120px;',
    autoFocus: false,
    shouldRerenderOnTransaction: true,
  });

  const handleImageInsert = useCallback(
    (data: ImageInsertData) => {
      if (!data.src) {
        onChange({
          imageUrl: undefined,
          imageAlt: undefined,
          imageLibraryItemId: undefined,
        });
        setIsImageModalOpen(false);
        return;
      }

      const trimmedAlt = data.alt.trim();
      onChange({
        imageUrl: data.src,
        imageAlt: trimmedAlt.length > 0 ? trimmedAlt : 'Image',
        imageLibraryItemId: data.libraryItemId,
      });
      setIsImageModalOpen(false);
    },
    [onChange]
  );

  const isSideLayout = layout === 'image-right' || layout === 'image-left';
  const isSideLayoutDesktop = isSideLayout && !isMobile;
  const useLeftInset = !isMobile && (layout === 'image-top' || layout === 'no-image');
  const hasImage = Boolean(block.imageUrl);
  const collapseImageHeight = hasImage;
  const splitWidths = getSplitColumnWidths(imageSize);
  const imageWrapperWidth = isMobile
    ? MOBILE_BLOCK_WIDTH
    : isSideLayoutDesktop
      ? splitWidths.image
      : IMAGE_TOP_WIDTH[imageSize];
  const imageWrapperMaxWidth = isMobile
    ? MOBILE_BLOCK_WIDTH
    : isSideLayoutDesktop
      ? splitWidths.image
      : DESKTOP_IMAGE_TOP_IMAGE_WIDTH;
  const imageMaxHeight = imageWrapperWidth;
  const textWrapperWidth = isMobile
    ? MOBILE_BLOCK_WIDTH
    : isSideLayoutDesktop
      ? splitWidths.text
      : DESKTOP_IMAGE_TOP_TEXT_WIDTH;

  const styles: Record<string, CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      width: '100%',
    },
    content: {
      display: 'flex',
      flexDirection: isSideLayoutDesktop ? 'row' : 'column',
      alignItems: isSideLayoutDesktop ? 'stretch' : useLeftInset ? 'flex-start' : 'center',
      justifyContent: 'flex-start',
      gap: isSideLayoutDesktop ? DESKTOP_IMAGE_RIGHT_GAP : tokens.spacing.lg,
      width: isSideLayoutDesktop ? DESKTOP_SPLIT_BLOCK_WIDTH : '100%',
      margin: isSideLayoutDesktop ? '0 auto' : 0,
    },
    imageWrapper: {
      position: 'relative',
      width: imageWrapperWidth,
      maxWidth: imageWrapperMaxWidth,
      marginLeft: !isSideLayoutDesktop && useLeftInset ? DESKTOP_LEFT_INSET : 0,
      borderRadius: tokens.radius.md,
      cursor: 'pointer',
      outline: imageAreaState.isFocused ? `2px solid ${tokens.ui.selectionBorder}` : 'none',
      outlineOffset: '2px',
      border: block.imageUrl ? 'none' : `1px solid ${tokens.borders.default}`,
      backgroundColor: 'transparent',
      flexShrink: 0,
    },
    imageInner: {
      position: 'relative',
      ...(collapseImageHeight
        ? {
            display: 'flex',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: layout === 'image-left' ? 'flex-end' : 'flex-start',
          }
        : {
            width: '100%',
            paddingBottom: isSideLayout ? '100%' : IMAGE_TOP_HEIGHT[imageSize],
            overflow: 'hidden',
          }),
    },
    imageSurface: {
      ...(collapseImageHeight
        ? {
            position: 'relative',
            display: 'block',
            maxWidth: '100%',
            width: '100%',
          }
        : {
            position: 'static',
          }),
    },
    image: {
      borderRadius: tokens.radius.md,
      ...(collapseImageHeight
        ? isSideLayout
          ? {
              display: 'block',
              width: '100%',
              maxHeight: imageMaxHeight,
            }
          : {
              display: 'block',
              width: '100%',
              height: 'auto',
            }
        : {
            position: 'absolute',
            bottom: isSideLayout ? undefined : 0,
            top: isSideLayout ? 0 : undefined,
            left: 0,
            maxWidth: '100%',
            maxHeight: '100%',
          }),
    },
    imageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: imageAreaState.isHovered || imageAreaState.isFocused ? 1 : 0,
      transition: 'opacity 150ms',
    },
    imageOverlayText: {
      color: tokens.text.onInverse,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      fontWeight: 600,
    },
    imagePlaceholder: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      color: tokens.text.muted,
      backgroundColor:
        imageAreaState.isHovered || imageAreaState.isFocused
          ? tokens.backgrounds.surfaceAlt
          : tokens.backgrounds.page,
      transition: 'background-color 150ms',
    },
    imagePlaceholderIcon: {
      width: '42px',
      height: '42px',
      color: tokens.text.muted,
    },
    imagePlaceholderText: {
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
    },
    textWrapper: {
      width: textWrapperWidth,
      maxWidth: textWrapperWidth,
      marginLeft: !isSideLayoutDesktop && useLeftInset ? DESKTOP_LEFT_INSET : 0,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    titleInput: getTitleInputStyle(titleLevel, tokens),
    bodyEditorContainer: {
      borderBottom: `1px solid ${tokens.borders.default}`,
      paddingBottom: tokens.spacing.sm,
      minHeight: '130px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      lineHeight: tokens.typography.body.lineHeight,
    },
  };

  const bodyResetStyles = `
    .basic-content-body-editor p {
      margin: 0;
    }
    .basic-content-body-editor p:not(:last-child) {
      margin-bottom: 0.5em;
    }
  `;

  const imageNode = shouldShowImage ? (
    <div
      role={translationMode ? undefined : 'button'}
      tabIndex={translationMode ? undefined : 0}
      aria-label={translationMode ? undefined : block.imageUrl ? 'Change image' : 'Add image'}
      style={{ ...styles.imageWrapper, ...(translationMode ? { cursor: 'default' } : {}) }}
      onClick={translationMode ? undefined : () => setIsImageModalOpen(true)}
      onKeyDown={translationMode ? undefined : (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsImageModalOpen(true);
        }
      }}
      onFocus={translationMode ? undefined : () => setImageAreaState((prev) => ({ ...prev, isFocused: true }))}
      onBlur={translationMode ? undefined : () => setImageAreaState((prev) => ({ ...prev, isFocused: false }))}
      onMouseEnter={translationMode ? undefined : () => setImageAreaState((prev) => ({ ...prev, isHovered: true }))}
      onMouseLeave={translationMode ? undefined : () => setImageAreaState((prev) => ({ ...prev, isHovered: false }))}
    >
      <div style={styles.imageInner}>
        {block.imageUrl ? (
          <>
            <div style={styles.imageSurface}>
              <img src={block.imageUrl} alt={block.imageAlt} style={styles.image} />
              {!translationMode ? (
                <div style={styles.imageOverlay}>
                  <span style={styles.imageOverlayText}>Click to change image</span>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div style={styles.imagePlaceholder}>
            <svg
              style={styles.imagePlaceholderIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
            <span style={styles.imagePlaceholderText}>{translationMode ? 'No source image' : 'Click to add image'}</span>
          </div>
        )}
      </div>
    </div>
  ) : null;

  const textNode = (
    <div style={styles.textWrapper}>
      {!translationMode ? (
        <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => onChange({ titleLevel: level as BasicContentTitleLevel })}
          tokens={tokens}
        />
      ) : null}

      <input
        type="text"
        value={block.title}
        onChange={(event) => onChange({ title: event.target.value })}
        placeholder="Enter title (optional)..."
        style={styles.titleInput}
        autoFocus={autoFocus}
        aria-label="Title text"
      />

      {translationMode && shouldShowImage ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing.xs }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: tokens.text.muted }}>Image alt text</span>
          <textarea
            value={block.imageAlt ?? ''}
            onChange={(event) => onChange({ imageAlt: event.target.value })}
            rows={2}
            aria-label="Image alt text"
            style={{
              width: '100%',
              padding: tokens.spacing.sm,
              border: `1px solid ${tokens.borders.default}`,
              borderRadius: tokens.radius.sm,
              backgroundColor: tokens.backgrounds.surface,
              color: tokens.text.primary,
              fontFamily: tokens.typography.fontFamily,
              boxSizing: 'border-box',
            }}
          />
        </label>
      ) : null}

      <style>{bodyResetStyles}</style>
      {bodyEditor ? (
        <div style={isBodyFocused ? {} : { opacity: 0.5, pointerEvents: 'none' as const }}>
          <RichTextToolbar
            editor={bodyEditor}
            items={fullPresetConfig.toolbarItems}
            toolbarStyle={toolbarStyles.toolbar}
            buttonStyle={toolbarStyles.button}
            activeButtonStyle={toolbarStyles.activeButton}
            dividerStyle={toolbarStyles.divider}
          />
        </div>
      ) : null}
      <div style={styles.bodyEditorContainer}>
        {bodyEditor ? <EditorContent editor={bodyEditor} /> : null}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} style={styles.container}>
      {!translationMode ? <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.lg, flexWrap: 'wrap' }}>
        <BasicContentLayoutToggle
          currentLayout={layout}
          onSelectLayout={onSetLayout}
        />

        {shouldShowImage ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{
              fontSize: '12px',
              color: tokens.text.muted,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase' as const,
            }}>
              Image Size:
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {IMAGE_SIZE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange({ imageSize: option.value })}
                  aria-pressed={imageSize === option.value}
                  title={`${option.value.charAt(0).toUpperCase()}${option.value.slice(1)} image`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px 10px',
                    borderRadius: tokens.radius.sm,
                    cursor: 'pointer',
                    fontSize: tokens.typography.body.size,
                    fontWeight: 500,
                    transition: 'all 150ms',
                    minWidth: '36px',
                    border: imageSize === option.value
                      ? `1px solid ${tokens.ui.selectionBorder}`
                      : `1px solid ${tokens.borders.default}`,
                    backgroundColor: imageSize === option.value
                      ? tokens.ui.selectionBorder
                      : '#fff',
                    color: imageSize === option.value
                      ? '#fff'
                      : tokens.text.muted,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div> : null}

      <div style={styles.content}>
        {isSideLayoutDesktop && layout === 'image-right' ? (
          <>
            {textNode}
            {imageNode}
          </>
        ) : (
          <>
            {imageNode}
            {textNode}
          </>
        )}
      </div>

      {!translationMode ? <ImageInsertModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onInsert={handleImageInsert}
        targetAspectRatio={LAYOUT_IMAGE_ASPECT_RATIOS[layout]}
        initialImage={
          block.imageUrl
            ? {
                src: block.imageUrl,
                alt: block.imageAlt,
              }
            : undefined
        }
      /> : null}
    </div>
  );
}

BasicContentBlockEditor.displayName = 'BasicContentBlockEditor';
